import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * The one overlay primitive.
 *
 * It replaces eight hand-rolled overlays. The old `Modal` did the hard mobile parts
 * correctly (safe-area padding, dvh sizing, scroll inside the card) and eight other
 * screens re-solved the same problem worse, each with its own backdrop colour and its
 * own z-index, so an overlay opened from inside a modal could render behind it.
 *
 * What none of them had, and this does:
 *   - `role="dialog"` and `aria-modal`, with the title as its accessible name
 *   - a focus trap, so Tab cannot reach the page behind
 *   - focus restored to whatever opened it on close
 *   - a body scroll lock, so the page does not scroll under the sheet on mobile
 *   - Escape read from a ref, so a re-rendered parent cannot leave a stale handler
 *   - one z-index, from the layer scale
 *
 * Audit references prevented here: shared.jsx #1, #2, #4, #5, #6; reminders.jsx #8;
 * calendar.jsx #14, #19; food.jsx #11; training.jsx #16; account.jsx #12.
 *
 * @param {object} props
 * @param {string} props.title            Accessible name. Rendered unless `hideTitle`.
 * @param {() => void} props.onClose
 * @param {import('react').ReactNode} props.children
 * @param {boolean} [props.hideTitle]     Keeps the name for screen readers only.
 * @param {number} [props.maxWidth]
 * @param {string} [props.scrimClassName] Adds layout styling to this modal's backdrop.
 * @param {'modal'|'drawer'} [props.variant] Uses an edge-attached surface when set to drawer.
 */
export function Modal({ title, onClose, children, hideTitle = false, maxWidth = 460, className = '', scrimClassName = '', variant = 'modal', headerContent = null }) {
  const titleId = useId();
  const cardRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Held in a ref so the key handler never closes over a stale callback. The old
  // Modal's Escape handler had an empty dependency array and captured the first
  // `onClose` it ever saw.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Remember what had focus, and give it back on close.
  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const card = cardRef.current;
    const firstFocusable = card?.querySelector(FOCUSABLE_SELECTOR);
    (firstFocusable ?? card)?.focus();

    return () => {
      const target = previouslyFocused.current;
      if (target instanceof HTMLElement && document.contains(target)) target.focus();
    };
  }, []);

  // Lock the page behind the sheet. The previous scroll position is restored, because
  // simply clearing `overflow` sends a mobile browser back to the top.
  useEffect(() => {
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = 'hidden';
    // Compensate for the scrollbar so the page behind does not shift sideways.
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
    };
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [...(cardRef.current?.querySelectorAll(FOCUSABLE_SELECTOR) ?? [])];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const scrimBaseClass = variant === 'drawer' ? 'drawer-scrim' : 'modal-scrim';
  const cardBaseClass = variant === 'drawer' ? 'drawer-card' : 'modal-card';

  return createPortal(
    <div
      className={`${scrimBaseClass} animate-scrim-in ${scrimClassName}`.trim()}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={cardRef}
        className={`${cardBaseClass} card animate-sheet-in ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ maxWidth }}
        onKeyDown={handleKeyDown}
      >
        <div className="modal-card__header">
          <h2 className={hideTitle ? 'visually-hidden' : 'modal-card__title'} id={titleId}>
            {title}
          </h2>
          <button type="button" className="icon-btn modal-card__close" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
          {headerContent}
        </div>
        <div className="modal-card__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/** Everything a keyboard can reach. Used by both the trap and the initial focus. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');
